import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Grid,
  Card,
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Input,
  InputLabel,
  IconButton,
  FormHelperText,
  Paper,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import { API_ENDPOINTS } from "../../../config/api"; // Ensure this is defined correctly

const CreateBrandForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    logo: null,
    publish: false,
  });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const [fileInputKey, setFileInputKey] = useState(0);
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    onClose: null,
  });
  const navigate = useNavigate();

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
    if (snackbar.onClose) snackbar.onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB");
        setFormData((prev) => ({ ...prev, logo: null }));
        setPreview(null);
        return;
      }
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        setFormData((prev) => ({ ...prev, logo: null }));
        setPreview(null);
        return;
      }
      setFormData((prev) => ({ ...prev, logo: file }));
      setError("");
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("publish", String(formData.publish)); // Convert to string if needed

      if (formData.logo) {
        formDataToSend.append("logo", formData.logo);
      }

      // Log formData contents
      for (let pair of formDataToSend.entries()) {
        console.log(`${pair[0]}:`, pair[1]);
      }

      const response = await axios.post(
        API_ENDPOINTS.BRANDS.CREATE,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setSnackbar({
        open: true,
        message: "Brand created successfully!",
        severity: "success",
        onClose: () => navigate("/brands"),
      });
    } catch (err) {
      let message =
        err.response?.data?.message || err.message || "Unknown error";
      // Friendly error for common backend constraint
      if (message.includes("foreign key constraint")) {
        message =
          "Cannot create brand due to related data constraints. Please check your input.";
      }
      setSnackbar({
        open: true,
        message: `Error creating brand: ${message}`,
        severity: "error",
        onClose: null,
      });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Add Brand
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <div className="image-field">
                    <Input
                      id="logo"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      key={fileInputKey}
                      sx={{ display: "none" }}
                    />
                    <label htmlFor="logo">
                      <Box
                        sx={{
                          border: "2px dashed #B0C4DE",
                          borderRadius: "10px",
                          padding: "20px",
                          textAlign: "center",
                          backgroundColor: "#F0F8FF",
                          cursor: "pointer",
                          minHeight: "120px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          position: "relative",
                        }}
                      >
                        {preview ? (
                          <>
                            <img
                              src={
                                formData.logo instanceof File
                                  ? preview
                                  : currentLogoUrl
                              }
                              alt="Uploaded"
                              style={{
                                maxWidth: "100%",
                                maxHeight: "80px",
                                marginBottom: "10px",
                                objectFit: "contain",
                              }}
                            />
                            <Typography
                              variant="body1"
                              sx={{ color: "#4682B4", fontWeight: "bold" }}
                            >
                              {formData.logo instanceof File
                                ? formData.logo.name
                                : "Current Logo"}
                            </Typography>
                            <IconButton
                              onClick={(e) => {
                                e.preventDefault();
                                setFormData((prev) => ({
                                  ...prev,
                                  logo: null,
                                }));
                                setPreview(null);
                              }}
                              sx={{
                                position: "absolute",
                                top: "5px",
                                right: "5px",
                                backgroundColor: "rgba(255, 255, 255, 0.8)",
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <UploadIcon
                              sx={{
                                fontSize: "40px",
                                color: "#4682B4",
                                marginBottom: "10px",
                              }}
                            />
                            <Typography
                              variant="body1"
                              sx={{ color: "#4682B4", fontWeight: "bold" }}
                            >
                              Upload Brand Logo
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#4682B4" }}
                            >
                              Supports: JPG, JPEG, PNG
                            </Typography>
                          </>
                        )}
                      </Box>
                    </label>
                  </div>
                  {error && <FormHelperText error>{error}</FormHelperText>}
                  <FormHelperText className="text-center">
                    Upload brand logo (PNG, JPG, JPEG, max 5MB)
                  </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={12}>
                <FormControl fullWidth>
                  <TextField
                    label="Brand Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="publish"
                      checked={formData.publish}
                      onChange={handleChange}
                    />
                  }
                  label="Publish"
                />
              </Grid>
            </Grid>

            <Box mt={2}>
              <Button type="submit" variant="contained">
                Create Brand
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={1000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateBrandForm;
